/**
 * VCL Step 6 — horizontal surface IDs and activation.
 *
 * Contract: docs/governance/VCL_HORIZONTAL_SURFACE_ADAPTER_CONTRACT.md
 * Only `customer` is active in this pilot. Admin / kiosk / pickup stay inactive —
 * no CSS apply, no panel, no global write-through.
 */

export type SurfaceId = 'customer' | 'admin' | 'kiosk' | 'pickup';

export type SurfaceActivation = 'active' | 'inactive';

export const SURFACE_IDS: readonly SurfaceId[] = [
  'customer',
  'admin',
  'kiosk',
  'pickup',
] as const;

export const SURFACE_ACTIVATION: Readonly<Record<SurfaceId, SurfaceActivation>> = {
  customer: 'active',
  admin: 'inactive',
  kiosk: 'inactive',
  pickup: 'inactive',
};

export function isSurfaceActive(surface: SurfaceId): boolean {
  return SURFACE_ACTIVATION[surface] === 'active';
}

/**
 * Guard for apply/panel paths: Step 6 may only mutate the customer surface.
 * Call before writing `--vcl-*` or mounting the DEV panel.
 */
export function assertCustomerSurface(surface: SurfaceId): asserts surface is 'customer' {
  if (surface !== 'customer' || !isSurfaceActive(surface)) {
    throw new Error(
      `VCL Step 6: surface "${surface}" is inactive. Only "customer" may apply VCL. ` +
        'Activate other surfaces only after per-surface inventory + regression cert ' +
        '(see VCL_HORIZONTAL_SURFACE_ADAPTER_CONTRACT.md).',
    );
  }
}

/** Alias used by applyVclCssVars / index (same guard; explicit annotation for TS assertion calls). */
export function assertCustomerVclSurface(surface: SurfaceId): asserts surface is 'customer' {
  assertCustomerSurface(surface);
}

/** Index-compatible aliases. */
export const VCL_ACTIVE_SURFACE = 'customer' as const;
export const VCL_SURFACE_ACTIVATION = SURFACE_ACTIVATION;
export const isVclSurfaceActive = isSurfaceActive;
